import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { PictureAsPdf, Visibility } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { listConsents } from '../../services/consents';
import { decryptFileFromS3, importKekFromBase64 } from '../../hooks/useDecryptedFiles';

interface ConsentFieldProps {
    /** The patient's consent personal-field value: empty, a `local-...`
     *  placeholder (not yet synced), or the EncryptedFile UUID once synced. */
    value?: string;
    label?: React.ReactNode;
}

/**
 * Read-only viewer for the patient "consent" personal field (type
 * `encrypted_document`). Mirrors the SkincAIr Flutter app's inline consent
 * widget: a status chip plus, when a signed consent exists, a button that
 * downloads + decrypts the EncryptedFile and opens the PDF.
 *
 * Capture/upload of consent is not done on web — this is view-only.
 */
export default function ConsentField({ value, label }: ConsentFieldProps) {
    const uuidInvestigation = useSelector(
        (state: any) => state.investigations?.currentInvestigation?.uuid
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trimmed = (value || '').trim();
    // A consent PDF still queued for upload on a field agent's device keeps a
    // `local-...` placeholder until sync swaps it for the EncryptedFile UUID.
    const isPending = trimmed.startsWith('local-');
    const hasConsent = trimmed.length > 0 && !isPending;

    const handleView = async () => {
        if (!uuidInvestigation) {
            setError('investigation');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // The consents KEK is unwrapped at login by authService.handleLoginKeks().
            const kekBase64 = localStorage.getItem(`kek_${uuidInvestigation}_consents`);
            if (!kekBase64) throw new Error('key');

            const resp = await listConsents(uuidInvestigation);
            const record = (resp.files || []).find(
                (f) => f.uuid === trimmed || f.localId === trimmed
            );
            if (!record) throw new Error('notfound');

            const kek = await importKekFromBase64(kekBase64);
            const decrypted = await decryptFileFromS3(
                uuidInvestigation,
                record.s3Key,
                record.nonce,
                kek
            );
            const blobUrl = URL.createObjectURL(
                new Blob([decrypted], { type: 'application/pdf' })
            );
            window.open(blobUrl, '_blank');
            // Give the new tab time to load before releasing the blob.
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            setError('open');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ my: 1 }} data-testid="consent-field">
            {label && (
                <Typography variant="h6" color="textPrimary">
                    {label}:
                </Typography>
            )}
            {!hasConsent && !isPending && (
                <Typography variant="body2" color="text.secondary">
                    <Translate id="consent.field.none" />
                </Typography>
            )}
            {isPending && (
                <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={<Translate id="consent.field.pending" />}
                />
            )}
            {hasConsent && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<PictureAsPdf />}
                        label={<Translate id="consent.field.recorded" />}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        data-testid="consent-view-btn"
                        startIcon={loading ? <CircularProgress size={16} /> : <Visibility />}
                        onClick={handleView}
                        disabled={loading}
                    >
                        <Translate id="consent.field.view" />
                    </Button>
                </Box>
            )}
            {error && (
                <Typography variant="body2" color="error">
                    <Translate id="consent.field.error" />
                </Typography>
            )}
        </Box>
    );
}
